import {
  PutRequest,
  useLazySamplesDustDensitySampleListQuery,
  useSamplesDustDensitySampleDeleteMutation,
  useSamplesDustDensitySampleListQuery,
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleShowQuery,
} from "@/services/backend/samples/api/dust-density-sample-api";
import { DeleteRequest } from "@/services/backend/samples/api/air-microbial-api";
import { DustDensitySample } from "@/services/backend/samples/types/dust-density-sample";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGetAll,
  documentDbGetAllDeleted,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  useDocumentDBDelete,
  useDocumentDBGet,
  useDocumentDBGetAll,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useDustDensitySampleOffline prepares two functions. One to
 * prepare the dust density samples for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useDustDensitySampleOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesDustDensitySampleListQuery();
  const [put] = useSamplesDustDensitySamplePutMutation();
  const [doDelete] = useSamplesDustDensitySampleDeleteMutation();

  const prepMsg = t("Speichere Staubdichteproben ...");
  const syncMsg = t("Synchronisiere Staubdichteproben ...");
  const doneMsg = t("Staubdichteproben");

  const prepareDustDensitySamples = async () => {
    // Load the latest state
    const result = await load({
      document: { active: true, values: [workOrderId] },
    });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("dustDensitySample");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await Promise.all(
      result.data!.samples.map(async (sample) => {
        await documentDbPut<DustDensitySample>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "dustDensitySample",
          sample,
        );
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncDustDensitySamples = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbSamples = await documentDbGetAll<DustDensitySample>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "dustDensitySample",
    );
    const deletedIdbSamples = await documentDbGetAllDeleted<DustDensitySample>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "dustDensitySample",
    );

    // Sync to server
    // We first PUT all non-deleted or new entities back to the server
    await Promise.all(
      idbSamples.map(async (sample) => {
        await put(sample).unwrap();
      }),
    );
    // Then we delete the entities that where deleted offline.
    await Promise.all(
      deletedIdbSamples.map(async (sample) => {
        await doDelete({ id: sample.id }).unwrap();
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { prepareDustDensitySamples, syncDustDensitySamples };
}

export function usePutDustDensitySample(): Mutation<
  DustDensitySample,
  DustDensitySample
> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [putDustDensitySampleMutation, { isLoading, error, isSuccess, reset }] =
    useSamplesDustDensitySamplePutMutation();

  const [
    putDustDensitySampleIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<PutRequest, DustDensitySample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "dustDensitySample",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        putDustDensitySampleMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        putDustDensitySampleIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetDustDensitySample(id: string) {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesDustDensitySampleShowQuery({
    id,
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<DustDensitySample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "dustDensitySample",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useListDustDensitySamples() {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesDustDensitySampleListQuery({
    document: {
      active: true,
      mode: FilterMode.Or,
      values: [workOrderId!],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<DustDensitySample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "dustDensitySample",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.samples,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useDeleteDustDensitySample(): Mutation<DeleteRequest, null> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [
    deleteDustDensitySampleMutation,
    { isLoading, error, isSuccess, reset },
  ] = useSamplesDustDensitySampleDeleteMutation();

  const [
    deleteDustDensitySampleIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBDelete(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "dustDensitySample",
  );
  const deleteIdb = (request: DeleteRequest) =>
    deleteDustDensitySampleIndexedDB(request.id);

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        deleteDustDensitySampleMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        deleteIdb,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Delete of sample not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}
